import type { StorefrontProductSummary } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { getRecent } from '../../lib/recentlyViewed.js';

import { useBuyerPricing } from './buyerPricing.js';
import { ProductCard } from './ProductCard.js';

/**
 * "Recently viewed" strip — renders the customer's recently-opened products
 * (from localStorage) as a card grid. Renders nothing when there's no history.
 */
export function RecentlyViewed({ excludeId, max = 4 }: { excludeId?: string; max?: number }): JSX.Element | null {
  const { t } = useTranslation('cms');
  const { slug } = useStorefront();
  const { currencyCode, authoritative } = useBuyerPricing();
  const [items, setItems] = useState<StorefrontProductSummary[]>([]);

  useEffect(() => {
    // These are SNAPSHOTS taken when the product was opened, so a shopper who has
    // since signed in (or out) would see prices from the other basis. Only show
    // the ones already denominated in their current currency.
    const priced = getRecent(slug, excludeId).filter((p) => !authoritative || p.currencyCode === currencyCode);
    setItems(priced.slice(0, max));
  }, [slug, excludeId, max, currencyCode, authoritative]);

  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold">{t('store.recentlyViewed', { defaultValue: 'Recently viewed' })}</h2>
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-4">
        {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </section>
  );
}
